import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { environment } from '@app/env';

interface ConsentSettings {
  analytics: boolean;
  marketing: boolean;
}

type ConsentUpdate = Partial<ConsentSettings>;

interface DataLayerWindow extends Window {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  lintrk?: (event: string, payload: Record<string, unknown>) => void;
  empSetCookieConsent?: (consent: ConsentUpdate) => void;
  empGetCookieConsent?: () => ConsentSettings;
}

type GoogleTagStatus = 'idle' | 'loading' | 'loaded' | 'failed';

const CONSENT_STORAGE_KEY = 'emp-cookie-consent-v1';
const GOOGLE_TAG_SCRIPT_ID = 'emp-google-ads-gtag';
const GTM_SCRIPT_ID = 'emp-gtm-script';
const LINKEDIN_SCRIPT_ID = 'emp-linkedin-insight';
const AHREFS_SCRIPT_ID = 'emp-ahrefs-analytics';
const AHREFS_DATA_KEY = 'rjEpeqwYPsc3H81Dh4H5Cg';
const AHREFS_SCRIPT_SRC = 'https://analytics.ahrefs.com/analytics.js';

const DEFAULT_CONSENT_VALUE: 'granted' | 'denied' = 'denied';
const DEFAULT_CONSENT: ConsentSettings = {
  analytics: false,
  marketing: false
};

@Injectable({
  providedIn: 'root'
})
export class TrackingConsentService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly gtmContainerId = environment.tracking.gtmContainerId;
  private readonly linkedinPartnerId = environment.tracking.linkedinPartnerId;
  private readonly googleAdsId = environment.tracking.googleAdsId;

  private initialized = false;
  private consent: ConsentSettings = DEFAULT_CONSENT;
  private hasStoredDecision = false;
  private googleTagStatus: GoogleTagStatus = 'idle';

  public initialize(): void {
    if (this.initialized || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.initialized = true;
    this.ensureDataLayerAndGtag();
    this.applyDefaultGoogleConsent();
    this.restoreStoredConsent();
    this.loadGoogleTag();
    this.configureGoogleAds();
    this.exposeConsentApi();
    this.registerConsentEventListener();
  }

  public updateConsent(consentUpdate: ConsentUpdate): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const nextConsent: ConsentSettings = {
      analytics: typeof consentUpdate.analytics === 'boolean' ? consentUpdate.analytics : this.consent.analytics,
      marketing: typeof consentUpdate.marketing === 'boolean' ? consentUpdate.marketing : this.consent.marketing
    };

    this.consent = {
      ...nextConsent
    };
    this.hasStoredDecision = true;

    this.persistConsent();
    this.applyGoogleConsent(this.consent);

    if (this.consent.analytics || this.consent.marketing) {
      this.loadGtmContainer();
    }

    if (this.consent.analytics) {
      this.loadAhrefsAnalytics();
    }

    if (this.consent.marketing) {
      this.loadLinkedInInsight();
    }
  }

  public hasMarketingConsent(): boolean {
    return this.consent.marketing;
  }

  public hasAnalyticsConsent(): boolean {
    return this.consent.analytics;
  }

  public hasStoredConsentDecision(): boolean {
    return this.hasStoredDecision;
  }

  public getGoogleTagStatus(): GoogleTagStatus {
    return this.googleTagStatus;
  }

  public acceptAll(): void {
    this.updateConsent({ analytics: true, marketing: true });
  }

  public rejectNonEssential(): void {
    this.updateConsent({ analytics: false, marketing: false });
  }

  private restoreStoredConsent(): void {
    const windowRef = this.getWindow();
    if (!windowRef) {
      return;
    }

    try {
      const rawStoredConsent = windowRef.localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!rawStoredConsent) {
        return;
      }

      const parsedConsent = JSON.parse(rawStoredConsent) as ConsentUpdate;
      this.hasStoredDecision = true;
      this.updateConsent({
        analytics: Boolean(parsedConsent.analytics),
        marketing: Boolean(parsedConsent.marketing)
      });
    } catch {
      this.hasStoredDecision = false;
      this.consent = DEFAULT_CONSENT;
      windowRef.localStorage.removeItem(CONSENT_STORAGE_KEY);
    }
  }

  private persistConsent(): void {
    const windowRef = this.getWindow();
    if (!windowRef) {
      return;
    }

    windowRef.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(this.consent));
  }

  private ensureDataLayerAndGtag(): void {
    const windowRef = this.getWindow();
    if (!windowRef) {
      return;
    }

    windowRef.dataLayer = windowRef.dataLayer || [];
    windowRef.gtag =
      windowRef.gtag ||
      function gtagShim() {
        // eslint-disable-next-line prefer-rest-params
        windowRef.dataLayer.push(arguments);
      };
  }

  private applyDefaultGoogleConsent(): void {
    const windowRef = this.getWindow();
    if (!windowRef?.gtag) {
      return;
    }

    windowRef.gtag('js', new Date());
    windowRef.gtag('consent', 'default', {
      ad_storage: DEFAULT_CONSENT_VALUE,
      analytics_storage: DEFAULT_CONSENT_VALUE,
      ad_user_data: DEFAULT_CONSENT_VALUE,
      ad_personalization: DEFAULT_CONSENT_VALUE,
      functionality_storage: 'granted',
      security_storage: 'granted',
      wait_for_update: 500
    });
  }

  private applyGoogleConsent(consent: ConsentSettings): void {
    const windowRef = this.getWindow();
    if (!windowRef?.gtag) {
      return;
    }

    const analyticsStorage = consent.analytics ? 'granted' : DEFAULT_CONSENT_VALUE;
    const marketingStorage = consent.marketing ? 'granted' : DEFAULT_CONSENT_VALUE;

    windowRef.gtag('consent', 'update', {
      analytics_storage: analyticsStorage,
      ad_storage: marketingStorage,
      ad_user_data: marketingStorage,
      ad_personalization: marketingStorage
    });
  }

  private loadGoogleTag(): void {
    if (!this.googleAdsId) {
      return;
    }

    const existingScript = this.document.getElementById(GOOGLE_TAG_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      return;
    }

    const script = this.document.createElement('script');
    script.id = GOOGLE_TAG_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(this.googleAdsId)}`;
    this.googleTagStatus = 'loading';
    script.addEventListener('load', () => {
      this.googleTagStatus = 'loaded';
      console.info('[Tracking] Google tag script loaded.', { googleAdsId: this.googleAdsId });
    });
    script.addEventListener('error', () => {
      this.googleTagStatus = 'failed';
      console.error('[Tracking] Failed to load Google tag script.', {
        googleAdsId: this.googleAdsId,
        scriptSrc: script.src
      });
    });
    this.document.head.appendChild(script);
  }

  private configureGoogleAds(): void {
    const windowRef = this.getWindow();
    if (!windowRef?.gtag || !this.googleAdsId) {
      return;
    }

    windowRef.gtag('config', this.googleAdsId);
  }

  private loadGtmContainer(): void {
    if (!this.gtmContainerId || this.document.getElementById(GTM_SCRIPT_ID)) {
      return;
    }

    const windowRef = this.getWindow();
    if (!windowRef) {
      return;
    }

    windowRef.dataLayer = windowRef.dataLayer || [];
    windowRef.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    const script = this.document.createElement('script');
    script.id = GTM_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(this.gtmContainerId)}`;
    this.document.head.appendChild(script);
  }

  // added by the request of Arnaud 12 march 2026 (teams chat)
  private loadAhrefsAnalytics(): void {
    if (this.document.getElementById(AHREFS_SCRIPT_ID)) {
      return;
    }

    const script = this.document.createElement('script');
    script.id = AHREFS_SCRIPT_ID;
    script.async = true;
    script.src = AHREFS_SCRIPT_SRC;
    script.setAttribute('data-key', AHREFS_DATA_KEY);
    this.document.head.appendChild(script);
  }

  private loadLinkedInInsight(): void {
    if (!this.linkedinPartnerId || this.document.getElementById(LINKEDIN_SCRIPT_ID)) {
      return;
    }

    const windowRef = this.getWindow();
    if (!windowRef) {
      return;
    }

    windowRef.lintrk =
      windowRef.lintrk ||
      function linkedinTracker(event: string, payload: Record<string, unknown>) {
        const queued = windowRef.lintrk as unknown as { q?: unknown[] };
        queued.q = queued.q || [];
        queued.q.push([event, payload]);
      };

    const linkedInDataPartnerIds = (windowRef as unknown as { _linkedin_data_partner_ids?: string[] })
      ._linkedin_data_partner_ids;
    if (Array.isArray(linkedInDataPartnerIds)) {
      linkedInDataPartnerIds.push(this.linkedinPartnerId);
    } else {
      (windowRef as unknown as { _linkedin_data_partner_ids: string[] })._linkedin_data_partner_ids = [
        this.linkedinPartnerId
      ];
    }

    const script = this.document.createElement('script');
    script.id = LINKEDIN_SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
    this.document.head.appendChild(script);
  }

  private exposeConsentApi(): void {
    const windowRef = this.getWindow();
    if (!windowRef) {
      return;
    }

    windowRef.empSetCookieConsent = (consent: ConsentUpdate) => {
      this.updateConsent(consent);
    };

    windowRef.empGetCookieConsent = () => {
      return { ...this.consent };
    };
  }

  private registerConsentEventListener(): void {
    const windowRef = this.getWindow();
    if (!windowRef) {
      return;
    }

    windowRef.addEventListener('emp-consent-update', event => {
      const consentUpdate = (event as CustomEvent<ConsentUpdate>).detail;
      if (consentUpdate && typeof consentUpdate === 'object') {
        this.updateConsent(consentUpdate);
      }
    });
  }

  private getWindow(): DataLayerWindow | null {
    return this.document.defaultView as DataLayerWindow | null;
  }
}
